// @ts-ignore
import BufferIconCell from 'volta/components/cell/buffer-icon-cell';
// @ts-ignore
import CodeDescCell from 'volta/components/cell/code-desc-cell';
// @ts-ignore
import LabelCell from 'volta/components/cell/label-cell';
import SkuModel, { SkuResourceName } from 'volta/models/sku';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Task } from 'ember-concurrency'
import type IntlService from 'ember-intl/services/intl';
import type { IBvTableColumn } from 'volta/components/bv-table';

interface IArgs {
  skus: SkuModel[];
  selectedSkus?: SkuModel[];
  selectable: boolean;
  task?: Task<SkuModel[], any[]>;
  onSelect?: (selection: SkuModel[]) => void;
}

export default class ImpactedSkus extends Component<IArgs> {
  // Services
  // ~~~~~

  @service declare intl: IntlService;

  // Properties
  // ~~~~~

  @tracked selectedSkus: SkuModel[] = this.args.selectedSkus ?? [];
  @tracked sorts = [{ valuePath: 'partCode', isAscending: true }];
  resourceName = SkuResourceName;

  // Computed Properties
  // ~~~~~

  get skus() {
    return this.args.skus ?? [];
  }

  @sort('skus', 'sortProperties')
  declare sortedSkus: SkuModel[];

  get sortProperties() {
    return this.sorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  get columns(): IBvTableColumn[] {
    const intl = this.intl;
    return [
      {
        columnName: intl.t('warehouse'),
        valuePath: 'warehouse.description',
        sortKey: 'warehouseDescription',
        isResizable: true,
        width: 100,
        cellComponent: CodeDescCell,
        format: (model: SkuModel) => {
          return {
            code: model.warehouse.code,
            description: model.warehouse.description
          };
        }
      },
      {
        columnName: intl.t('part'),
        valuePath: 'skuDescription',
        sortKey: 'partCode',
        isResizable: true,
        width: 250,
        cellComponent: CodeDescCell,
        format: (model: SkuModel) => {
          return {
            code: model.part.code,
            description: model.skuDescription
          };
        }
      },
      {
        columnName: intl.t('planningMethodology'),
        valuePath: 'methodology',
        sortKey: 'methodology',
        isResizable: true,
        isReorderable: true,
        width: 50,
        cellComponent: BufferIconCell,
        format: (model: SkuModel) => {
          return {
            methodology: model.methodology,
            isProtected: model.isProtected
          };
        }
      },
      {
        columnName: intl.t('acqCodeShort'),
        sortKey: 'acqCode',
        valuePath: 'acqCode',
        width: 80,
        isReorderable: true,
        isResizable: true,
        cellComponent: LabelCell,
        format: (model: SkuModel) => {
          const acqCode = model.acqCode;
          return {
            label: intl.t(`acqCodes.${acqCode || 'UNKNOWN'}`)
          };
        }
      },
      {
        columnName: this.intl.t('leadTimes'),
        valuePath: 'vltDisplay',
        classNames: 'u-text-mono',
        textAlign: 'right',
        sortKey: 'voltaLeadTime',
        isResizable: true,
        isReorderable: true,
        minWidth: 25,
        width: 80
      }
    ];
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    const { task } = this.args;
    if (typeof task?.perform === 'function') {
      task.perform();
    }
  }

  // Actions
  // ~~~~~

  @action
  onSelect(selection: SkuModel[]) {
    this.selectedSkus = selection;
    this.args.onSelect?.(selection);
  }
}
